import React, { useState, useEffect } from 'react'

import { Tooltip, TextField, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material'
import ClearIcon from "@mui/icons-material/Clear";
import Swal from 'sweetalert2'
import Pusher from 'pusher-js'
import { Link } from 'react-router-dom'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import AppLayout from '../../components/Layouts/AppLayout'
import TableSharedCustom from '../../components/TableSharedCustom'
import TableSharedPaginated from '../../components/TableSharedPaginated';
import Loading from '../../components/Loading'
import AddButton from '../../components/AddButton'
import { useAuth } from '../../hooks/auth'
import { handleAxiosError, isValid } from '../../helpers/helper'
import { t } from 'i18next'
import { useNavigate } from "react-router-dom"
import CreateTemplate from './Create/CreateTemplate'
import { useDebounce } from 'use-debounce'
import { buildUrl } from '../../helpers/apiHelper';

const Templates = () => {
    const navigate = useNavigate()
    const { company_id, config, pusher } = useStateContext()
    const { user } = useAuth({ middleware: 'guest' })

    const [isLoading, setIsLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const [templates, setTemplates] = useState([])
    const [total, setTotal] = useState(0)
    const [code, setCode] = useState('')
    const [description, setDescription] = useState('')

    const initialSearch = {
        name: '',
        type: '',
        customer: '',
        shipAddress: '',
        vendor: '',
        purchaseAddress: '',
        company_id: ''
    }

    const getStoredSearch = () => {
        if (localStorage.getItem('filters') !== null) {
            const filters = JSON.parse(localStorage.getItem('filters'))
            return { ...initialSearch, ...(filters[0]?.templates_search || {}) }
        }

        return initialSearch
    }

    const [search, setSearch] = useState(getStoredSearch)
    const [debouncedSearch] = useDebounce(search, 500)

    const initialPagination = {
        page: 1,
        rowsPerPage: 10,
        sort: null
    }

    const [pagination, setPagination] = useState(initialPagination)

    const [model, setModel] = useState({
        actions: true,
        name: true,
        type: true,
        customer: true,
        ship_address: true,
        vendor: true,
        purchase_address: true,
        so_items: true,
        po_items: true
    })

    useEffect(() => {
        getTableView()
    }, [])

    useEffect(() => {
        const _company_id = Number(company_id);
        const _search_company_id = Number(search?.company_id);
        if(isValid(company_id) && _company_id !== _search_company_id) {
            setSearch((prev) => ({...prev, company_id: company_id}))
        }
    }, [company_id, search])

    const getTableView = async () => {
        setIsLoading(true)

        await axios
            .get(`/api/get-table-view?view_name=templates_table&user_id=${user?.id}`, config)
            .then(res => {
                const model = res.data
                if (Object.keys(model?.success?.data).length > 0) {
                    setModel(model.success.data)
                }
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        if(isValid(company_id)) {
            const channeldelete = pusher.subscribe(`${localStorage.getItem('client_id')}-transactiontemplate-deleted-company-${company_id}`)
            const channelcreate = pusher.subscribe(`${localStorage.getItem('client_id')}-transactiontemplate-created-company-${company_id}`)
            const channelupdate = pusher.subscribe(`${localStorage.getItem('client_id')}-transactiontemplate-updated-company-${company_id}`)

            channeldelete.bind(`${localStorage.getItem('client_id')}-transactiontemplate-deleted-event-company-${company_id}`, data => {
                getTemplates()
            })

            channelcreate.bind(`${localStorage.getItem('client_id')}-transactiontemplate-created-event-company-${company_id}`, data => {
                getTemplates()
            })

            channelupdate.bind(`${localStorage.getItem('client_id')}-transactiontemplate-updated-event-company-${company_id}`, data => {
                getTemplates()
            })
        }

        return (() => {
            if(isValid(company_id)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-transactiontemplate-deleted-company-${company_id}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-transactiontemplate-created-company-${company_id}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-transactiontemplate-updated-company-${company_id}`)
            }
        })
    }, [company_id])

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '13') {
                navigate("/templates/create")
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    useEffect(() => {
        if(!isValid(debouncedSearch?.company_id)) {
            return;
        }

        getTemplates(debouncedSearch, pagination)

        if (localStorage.getItem('filters') === null) {
            localStorage.setItem('filters', JSON.stringify([{}]))
        }

        const filters = JSON.parse(localStorage.getItem('filters'))
        filters[0].templates_search = debouncedSearch
        localStorage.setItem('filters', JSON.stringify(filters))
    }, [debouncedSearch, pagination])

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                setOpen(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    const getTemplates = async (_search, _pagination = initialPagination) => {
        setIsLoading(true)
        const formData = {}

        formData['name'] = _search?.name
        formData['type'] = _search?.type
        formData['customer'] = _search?.customer
        formData['vendor'] = _search?.vendor
        formData['ship_address'] = _search?.shipAddress
        formData['purchase_address'] = _search?.purchaseAddress
        formData['company_id'] = _search?.company_id

        let url = buildUrl(
            `/api/templates/list`,
            {
                where: {
                    'page': _pagination.page,
                    'page_size': _pagination.rowsPerPage
                }
            }
        )

        if(_pagination?.sort) {
            url = url.concat(`&sort=${_pagination.sort?.field},${_pagination.sort?.sort}`)
        }

        await axios
            .post(url, formData, config)
            .then(({ data }) => {
                const { data: _data, total: _total } = data;
                setTemplates(_data);
                setTotal(_total);

                const maxPage = Math.max(1, Math.ceil(_total / _pagination.rowsPerPage))
                if (_pagination.page > maxPage) {
                    setPagination((prev) => (
                        prev.page === _pagination.page && prev.rowsPerPage === _pagination.rowsPerPage
                            ? { ...prev, page: 1 }
                            : prev
                    ))
                }
            })
            .finally(() => setIsLoading(false))
    }

    const columns = [
        {
            field: 'name',
            headerName: t('template_name'),
            flex: 1
        },
        {
            field: 'type',
            headerName: t('order_type'),
            flex: 1,
            renderCell: (params) => t(params.value)
        },
        {
            field: 'customer',
            headerName: t('customer_or_service_provider'),
            flex: 1,
            renderCell: (params) => params.value?.name || ''
        },
        {
            field: 'ship_address',
            headerName: t('ship_to_location'),
            flex: 1,
            renderCell: (params) => params.value?.address || ''
        },
        {
            field: 'vendor',
            headerName: t('supplier'),
            flex: 1,
            renderCell: (params) => params.value?.name || ''
        },
        {
            field: 'purchase_address',
            headerName: t('purchase_location'),
            flex: 1,
            renderCell: (params) => params.value?.address || ''
        },
        {
            field: 'so_items',
            headerName: t('so_items'),
            flex: 1
        },
        {
            field: 'po_items',
            headerName: t('po_items'),
            flex: 1
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.5,
            cellClassName: 'padding-0',
            renderCell: (params) => <TemplateActions params={params} config={config} setIsLoading={setIsLoading} />
        }
    ]

    const handleOpen = () => {
        setOpen(!open)
    }

    const handlePagination = (newPage, newRowsPerPage) => {
        setPagination((prev) => ({
            ...prev,
            page: newPage,
            rowsPerPage: newRowsPerPage
        }));
    }

    const handleSort = (newSort) => {
        setPagination((prev) => ({
            ...prev,
            sort: newSort
        }))
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-5 pt-0'>
                    <div className='pb-5 bg-white mb-2 rounded-md'>
                        <div className='flex justify-between items-center p-5 border-b'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('templates')}</p>
                            <Tooltip disableInteractive title={t('create_new_x', {x: t('template')})} placement='bottom'>
                                <div>
                                    <AddButton onClick={handleOpen}><i className="fa-solid fa-plus"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>

                        <div className='flex flex-wrap justify-between items-end w-full border-b pb-5'>
                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <div className='flex justify-between items-center search'>
                                    <input
                                        type="text"
                                        placeholder={t('search_by_x', {x: t('template_name')})}
                                        value={search.name}
                                        className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place'
                                        onChange={(e) => setSearch((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel id="demo-simple-select-standard-label">{t('search_by_x', {x: t('order_type')})}</InputLabel>
                                    <Select
                                        displayEmpty
                                        value={search.type}
                                        onChange={(e) => setSearch((prev) => ({ ...prev, type: e.target.value }))}
                                        label={t('search_by_x', {x: t('customer')})}
                                        sx={{
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                            ".MuiSelect-iconOutlined": { display: search.type ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                        }}
                                        endAdornment={search.type ? (<IconButton sx={{ visibility: search.type ? "visible" : "hidden", padding: '0' }} onClick={(e) => setSearch((prev) => ({ ...prev, type: '' }))}><ClearIcon /></IconButton>) : false}
                                    >
                                        <MenuItem value="shipment">{t('shipment')}</MenuItem>
                                        <MenuItem value="collection">{t('collection')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2 lg:w-1/4'>
                                <div className='flex justify-between items-center search'>
                                    <input
                                        type="text"
                                        placeholder={t('search_by_x', {x: t('customer_service_provider')})}
                                        value={search.customer}
                                        className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place'
                                        onChange={(e) => setSearch((prev) => ({ ...prev, customer: e.target.value }))}
                                    />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2 lg:w-1/4'>
                                <div className='flex justify-between items-center search'>
                                    <input
                                        type="text"
                                        placeholder={t('search_by_x', {x: t('ship_to_location')})}
                                        value={search.shipAddress}
                                        className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place'
                                        onChange={(e) => setSearch((prev) => ({ ...prev, shipAddress: e.target.value }))}
                                    />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2 lg:w-1/4'>
                                <div className='flex justify-between items-center search'>
                                    <input
                                        type="text"
                                        placeholder={t('search_by_x', {x: t('supplier')})}
                                        value={search.vendor}
                                        className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place'
                                        onChange={(e) => setSearch((prev) => ({ ...prev, vendor: e.target.value }))}
                                    />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2 lg:w-1/4'>
                                <div className='flex justify-between items-center search'>
                                    <input
                                        type="text"
                                        placeholder={t('search_by_x', {x: t('purchase_addresse')})}
                                        value={search.purchaseAddress}
                                        className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place'
                                        onChange={(e) => setSearch((prev) => ({ ...prev, purchaseAddress: e.target.value }))}
                                    />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>
                        </div>

                        <div className='pt-3'>
                            <TableSharedPaginated
                                pagination={pagination}
                                handlePagination={handlePagination}
                                total={total}
                                items={templates}
                                columns={columns}
                                columnVisibilityModel={model}
                                setModel={setModel}
                                view='templates'
                                setSort={handleSort}
                            />
                        </div>
                    </div>

                </div>

                <CreateTemplate
                    open={open}
                    handleOpen={handleOpen}
                    setIsLoading={setIsLoading}
                />
            </AppLayout>
        </>
    )
}

export default Templates

const TemplateActions = ({ params, config, setIsLoading }) => {
    const deleteTemplate = async (id) => {
        const isConfirm = await Swal.fire({
            title: `${t('title_delete')} ${t('del_template')} ${params.row.name}?`,
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        })
        .then((result) => result.isConfirmed)

        if (!isConfirm) {
            return
        }

        setIsLoading(true)

        await axios.delete(`/api/templates/${id}`, params.config)
        .then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data.success.message
            })
        })
        .catch(({ response }) => handleAxiosError({ response: response }))
        .finally(() => setIsLoading(false))
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('edit')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <Link to={`${params.row.id}`}>
                            <span
                                style={{ cursor: 'pointer' }}
                                className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"
                            >
                                <i className="fa-solid fa-pencil"></i>
                            </span>
                        </Link>
                    </div>
                </Tooltip>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span
                            style={{ cursor: 'pointer' }}
                            className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"
                            onClick={() => deleteTemplate(params.row.id)}
                        >
                            <i className="fa-solid fa-trash"></i>
                        </span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}


